import { Calendar, CircleAlert } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Navigate, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom'

import {
  type AppointmentTypeId,
  addCalendarDays,
  CLINICS_BY_ID,
  type ClinicId,
  clinicIdSchema,
  DEFAULT_TIMEZONE,
  requireAppointmentType,
  toWallClockDate,
} from '@snootsvet/shared/portal'

import { BookingContextCard } from '@/customer/components/booking/booking-context-card'
import { BookingNotes } from '@/customer/components/booking/booking-notes'
import { getMondayOfWeek, WeekSlotPicker } from '@/customer/components/booking/week-slot-picker'
import { ContactSupportAction } from '@/customer/components/contact-support-action'
import { PageContainer } from '@/customer/components/page-container'
import type { PetScopeContext } from '@/customer/components/pet-scope'
import { Alert, AlertAction, AlertDescription, AlertTitle } from '@/customer/components/ui/alert'
import { Button } from '@/customer/components/ui/button'
import { useBookAppointment } from '@/customer/hooks/use-book-appointment'
import { useDocumentTitle } from '@/customer/hooks/use-document-title'
import { useGetAvailableSlots } from '@/customer/hooks/use-get-available-slots'
import { useListPetAppointmentTypes } from '@/customer/hooks/use-list-pet-appointment-types'
import { useRegion } from '@/customer/hooks/use-region'
import { MEMBER_VISIBLE_APPOINTMENT_TYPE_IDS } from '@/customer/lib/appointments'
import { routes } from '@/customer/routes'

/**
 * Portal slot-picker page. Reached from the "Book a visit" dialog
 * with the pre-filter values in the URL:
 *
 *   /account/pets/:patientId/book?type=<AppointmentTypeId>&clinic=<ClinicId>
 *
 * Notes are collected here (not in the dialog) so the URL contract
 * stays clean and the freeform copy sits alongside the actual slot
 * choice. Any invalid URL param (unknown patient, non-bookable type,
 * unknown clinic) bounces the member back to the account home - the
 * dialog is the canonical way to arrive here, so we don't try to
 * gracefully recover from hand-typed URLs.
 */
export function BookAppointmentPage() {
  useDocumentTitle('Book a visit')

  const { pet } = useOutletContext<PetScopeContext>()
  const [searchParams] = useSearchParams()

  const rawType = searchParams.get('type') ?? ''
  const rawClinic = searchParams.get('clinic') ?? ''

  // Query-param guards. `<PetScope>` has already resolved the pet
  // and `PetSubscriptionGuard` has verified subscription + member
  // readiness, so we only need to validate the free-form `type` /
  // `clinic` params here. Invalid combos bounce home - the
  // pre-filter dialog is the only supported entry point.
  const clinicParse = clinicIdSchema.safeParse(rawClinic)
  const clinic = clinicParse.success ? CLINICS_BY_ID[clinicParse.data] : null
  const appointmentTypeId = MEMBER_VISIBLE_APPOINTMENT_TYPE_IDS.some((id) => id === rawType)
    ? (rawType as AppointmentTypeId)
    : null

  if (!clinic || !appointmentTypeId) {
    return <Navigate to={routes.account.index} replace />
  }

  return (
    <BookAppointmentPageInner
      patient={pet}
      patientName={pet.name}
      clinicId={clinic.id}
      timezone={clinic.timezone ?? DEFAULT_TIMEZONE}
      appointmentTypeId={appointmentTypeId}
    />
  )
}

/**
 * Inner half of the booking page. Split out so the guards can early-
 * return before we mount slot/booking hooks - keeps `useGetAvailableSlots`
 * from firing with dummy params during the guard phase.
 */
function BookAppointmentPageInner({
  patient,
  patientName,
  clinicId,
  timezone,
  appointmentTypeId,
}: {
  patient: { id: string }
  patientName: string
  clinicId: ClinicId
  timezone: string
  appointmentTypeId: AppointmentTypeId
}) {
  const navigate = useNavigate()
  const region = useRegion()
  const bookAppointment = useBookAppointment()
  const petBookingOptions = useListPetAppointmentTypes(patient.id)
  const appointmentType = requireAppointmentType(appointmentTypeId)
  const appointmentTypeAllowed =
    appointmentType.memberBooking === 'self-service' &&
    petBookingOptions.data?.appointmentTypeIds.includes(appointmentTypeId)

  // Anchored on the clinic's current date, not the browser's, so a
  // member browsing from another timezone still lands on the week the
  // clinic is in.
  const minWeekStart = useMemo(
    () => getMondayOfWeek(toWallClockDate(new Date(), timezone)),
    [timezone]
  )
  const [weekStart, setWeekStart] = useState(minWeekStart)
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null)
  const [notes, setNotes] = useState('')

  const slotsQuery = useGetAvailableSlots(
    {
      clinicId,
      appointmentTypeId,
      startDate: weekStart,
      endDate: addCalendarDays(weekStart, 7),
    },
    { enabled: appointmentTypeAllowed === true }
  )

  if (petBookingOptions.data && !appointmentTypeAllowed) {
    return <Navigate to={routes.account.index} replace />
  }

  if (petBookingOptions.isError || slotsQuery.isError) {
    return (
      <PageContainer
        backLink={{ to: routes.account.index, label: 'Back' }}
        headline={[
          { text: 'Pick a time', color: 'yellow' },
          { text: `for ${patientName}`, color: 'blue' },
        ]}
      >
        <Alert variant="warning">
          <AlertTitle>We couldn&apos;t load appointment availability</AlertTitle>
          <AlertDescription>
            Please try again shortly. If you need to book urgently, contact the care team and
            we&apos;ll sort you out.
          </AlertDescription>
          <AlertAction>
            <ContactSupportAction />
          </AlertAction>
        </Alert>
      </PageContainer>
    )
  }

  const handleConfirm = () => {
    if (!selectedSlot) return
    const trimmedNotes = notes.trim()
    bookAppointment.mutate(
      {
        patientId: patient.id,
        clinicId,
        appointmentTypeId,
        startTime: selectedSlot,
        userNotes: trimmedNotes ? trimmedNotes : undefined,
      },
      {
        onSuccess: () => {
          navigate(routes.account.index)
        },
      }
    )
  }

  const bookError = bookAppointment.error
  const isBooking = bookAppointment.isPending

  return (
    <PageContainer
      backLink={{ to: routes.account.index, label: 'Back' }}
      headline={[
        { text: 'Pick a time', color: 'yellow' },
        { text: `for ${patientName}`, color: 'blue' },
      ]}
    >
      <div className="flex flex-col gap-8">
        <BookingContextCard
          appointmentTypeId={appointmentTypeId}
          clinicId={clinicId}
          region={region}
        />

        <WeekSlotPicker
          slots={slotsQuery.data?.slots}
          selectedSlot={selectedSlot}
          onSelectSlot={setSelectedSlot}
          weekStart={weekStart}
          onWeekChange={setWeekStart}
          minWeekStart={minWeekStart}
          timezone={timezone}
          region={region}
          loading={petBookingOptions.isFetching || slotsQuery.isFetching}
        />

        <BookingNotes id="booking-notes" value={notes} onChange={setNotes} />

        {bookError ? (
          <Alert variant="destructive">
            <CircleAlert />
            <AlertTitle>
              {bookError.message || 'Something went wrong. Please try again.'}
            </AlertTitle>
          </Alert>
        ) : null}

        <div className="flex flex-col gap-3 sm:flex-row-reverse">
          <Button
            type="button"
            className="w-full sm:flex-1"
            disabled={!selectedSlot || isBooking}
            onClick={handleConfirm}
          >
            <Calendar data-icon="inline-start" />
            {isBooking ? 'Booking…' : 'Confirm booking'}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full sm:flex-1"
            onClick={() => navigate(routes.account.index)}
            disabled={isBooking}
          >
            Cancel
          </Button>
        </div>
      </div>
    </PageContainer>
  )
}
